import { useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { authClient } from "@/auth/client";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  SquaresFour,
  Users,
  User as UserRound,
  Briefcase,
  Calendar,
  Buildings,
  FileText,
  Files,
  GearSix,
  Envelope,
  SignOut,
  List,
  X,
  SidebarSimple,
  Stethoscope,
  Money,
  ChartLineUp,
  Receipt,
  CalendarDots,
  ClipboardText,
  CreditCard,
  Star,
  ListChecks,
  XCircle,
  ArrowCounterClockwise,
  Notepad,
  VideoCamera,
  type Icon,
} from "@phosphor-icons/react";
import { Logo, LogoMark } from "@/components/Logo";
import { SubscriptionBanner } from "@/components/dashboard/SubscriptionBanner";
import type { TFunction } from "i18next";

interface SidebarItem {
  label: string;
  href: string;
  icon: Icon;
}

interface SidebarSection {
  label?: string;
  items: SidebarItem[];
}

export const getAdminSections = (t: TFunction): SidebarSection[] => [
  {
    items: [
      { label: t("sidebar.dashboard"), href: "/dashboard", icon: SquaresFour },
    ],
  },
  {
    label: t("sidebar.groups.operations"),
    items: [
      { label: t("sidebar.therapies"), href: "/dashboard/admin/therapies", icon: Stethoscope },
      { label: t("sidebar.calendar"), href: "/dashboard/admin/calendar", icon: CalendarDots },
      { label: t("sidebar.payouts"), href: "/dashboard/admin/payouts", icon: Money },
      { label: t("sidebar.reports"), href: "/dashboard/admin/reports", icon: ChartLineUp },
      { label: t("sidebar.cancellationRequests"), href: "/dashboard/admin/cancellation-requests", icon: XCircle },
      { label: "Povrati", href: "/dashboard/admin/refunds", icon: ArrowCounterClockwise },
      { label: t("sidebar.groupEnrollments"), href: "/dashboard/admin/group-enrollments", icon: Users },
      { label: "Pretplate", href: "/dashboard/admin/subscriptions", icon: CreditCard },
    ],
  },
  {
    label: t("sidebar.groups.people"),
    items: [
      { label: t("sidebar.therapists"), href: "/dashboard/admin/therapists", icon: Users },
      { label: t("sidebar.clients"), href: "/dashboard/admin/clients", icon: UserRound },
      { label: t("sidebar.companies"), href: "/dashboard/admin/companies", icon: Buildings },
    ],
  },
  {
    label: t("sidebar.groups.content"),
    items: [
      { label: t("sidebar.content"), href: "/dashboard/admin/content", icon: Files },
      { label: t("sidebar.catalog"), href: "/dashboard/admin/catalog", icon: ListChecks },
      { label: t("sidebar.blog"), href: "/dashboard/admin/blog", icon: FileText },
      { label: t("sidebar.videos"), href: "/dashboard/admin/videos", icon: VideoCamera },
      { label: t("sidebar.documents"), href: "/dashboard/admin/documents", icon: Notepad },
      { label: t("sidebar.reviews"), href: "/dashboard/admin/reviews", icon: Star },
    ],
  },
  {
    label: t("sidebar.groups.system"),
    items: [
      { label: t("sidebar.settings"), href: "/dashboard/admin/settings", icon: GearSix },
      { label: t("sidebar.email"), href: "/dashboard/admin/email-logs", icon: Envelope },
    ],
  },
];

export const getTherapistSections = (t: TFunction): SidebarSection[] => [
  {
    label: t("sidebar.groups.overview"),
    items: [
      { label: t("sidebar.dashboard"), href: "/dashboard/therapist", icon: SquaresFour },
      { label: t("sidebar.calendar"), href: "/dashboard/therapist/calendar", icon: CalendarDots },
    ],
  },
  {
    label: t("sidebar.groups.clientWork"),
    items: [
      { label: t("sidebar.therapies"), href: "/dashboard/therapist/therapies", icon: Stethoscope },
    ],
  },
  {
    label: t("sidebar.groups.offer"),
    items: [
      { label: t("sidebar.services"), href: "/dashboard/therapist/services", icon: Briefcase },
      { label: t("sidebar.groupTherapies"), href: "/dashboard/therapist/group-therapies", icon: Users },
      { label: t("sidebar.tests"), href: "/dashboard/therapist/tests", icon: ClipboardText },
    ],
  },
  {
    label: t("sidebar.groups.communication"),
    items: [
      { label: t("sidebar.blog"), href: "/dashboard/therapist/blog", icon: FileText },
      { label: t("sidebar.reviews"), href: "/dashboard/therapist/reviews", icon: Star },
    ],
  },
  {
    label: t("sidebar.groups.profileFinance"),
    items: [
      { label: t("sidebar.finances"), href: "/dashboard/therapist/finances", icon: Receipt },
      { label: t("sidebar.settings"), href: "/dashboard/therapist/settings", icon: GearSix },
    ],
  },
];

const getSupervisorSections = (t: TFunction): SidebarSection[] => [
  {
    items: [
      { label: t("sidebar.dashboard"), href: "/dashboard", icon: SquaresFour },
      { label: t("sidebar.employees"), href: "/dashboard/supervisor/employees", icon: Users },
      { label: t("sidebar.sessions"), href: "/dashboard/supervisor/sessions", icon: Calendar },
      { label: t("sidebar.billing"), href: "/dashboard/supervisor/billing", icon: Receipt },
      { label: t("sidebar.credit"), href: "/dashboard/supervisor/credit", icon: CreditCard },
    ],
  },
];

const getClientSections = (t: TFunction): SidebarSection[] => [
  {
    items: [
      { label: t("sidebar.dashboard"), href: "/dashboard", icon: SquaresFour },
      { label: t("sidebar.mySessions"), href: "/dashboard/client/sessions", icon: Calendar },
      { label: t("sidebar.groupTherapies"), href: "/dashboard/client/group-therapies", icon: Users },
    ],
  },
];

export function DashboardLayout({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  const role = user?.role as string | undefined;
  const sections = role === "admin"
    ? getAdminSections(t)
    : role === "therapist"
      ? getTherapistSections(t)
      : role === "supervisor"
        ? getSupervisorSections(t)
        : getClientSections(t);
  const whiteContentBg = role === "admin" || role === "therapist" || role === "supervisor";

  const isActive = (href: string) => {
    if (href === "/dashboard" || href === "/dashboard/therapist") return location.pathname === href;
    return location.pathname.startsWith(href);
  };

  const handleLogout = async () => {
    await authClient.signOut();
    window.location.href = "/login";
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-background">
      {/* ─── Mobile overlay ─── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm md:hidden animate-in fade-in duration-200"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ─── Sidebar (desktop + tablet) ─── */}
      <aside
        className={cn(
          "hidden md:flex flex-col shrink-0 border-r border-sidebar-border bg-sidebar transition-all duration-300 ease-in-out",
          collapsed ? "w-[64px]" : "w-[216px]"
        )}
      >
        {/* Header: wordmark + collapse toggle */}
        <div
          className={cn(
            "flex shrink-0 items-center",
            collapsed ? "flex-col gap-1.5 px-0 pt-3 pb-1" : "h-14 justify-between px-4"
          )}
        >
          <Link to="/dashboard" className="flex items-center">
            {collapsed ? (
              <LogoMark className="size-5" />
            ) : (
              <Logo className="h-5 w-auto text-sidebar-foreground" />
            )}
          </Link>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              "rounded-lg p-1.5 text-sidebar-foreground/40 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground",
              !collapsed && "-mr-1.5"
            )}
            aria-label={collapsed ? t("sidebar.expand") : t("sidebar.collapse")}
          >
            <SidebarSimple className="size-[18px]" />
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto px-2.5 pb-2.5">
          {sections.map((section, idx) => {
            const isFirst = idx === 0;
            return (
              <div key={idx}>
                {/* Group label (expanded) */}
                {section.label && !collapsed && (
                  <p
                    className={cn(
                      "px-2.5 pb-1 text-[11px] font-medium tracking-tight text-sidebar-foreground/40",
                      isFirst ? "pt-1" : "pt-3.5"
                    )}
                  >
                    {section.label}
                  </p>
                )}
                {/* Group divider (collapsed) */}
                {section.label && collapsed && !isFirst && (
                  <div className="mx-2.5 my-2 h-px bg-sidebar-border/60" />
                )}
                <ul className="flex flex-col gap-px">
                  {section.items.map((item) => {
                    const active = isActive(item.href);
                    const Icon = item.icon;
                    return (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          className={cn(
                            "group relative flex items-center rounded-lg text-[13px] font-medium transition-colors duration-150",
                            collapsed ? "justify-center px-0 py-2" : "gap-2.5 px-2.5 py-1.5",
                            active
                              ? "bg-sidebar-accent text-sidebar-foreground"
                              : "text-sidebar-foreground/65 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
                          )}
                          title={collapsed ? item.label : undefined}
                        >
                          {/* Active indicator bar */}
                          {active && !collapsed && (
                            <span className="absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-primary" />
                          )}
                          <Icon
                            weight={active ? "fill" : "regular"}
                            className={cn(
                              "size-[18px] shrink-0 transition-colors duration-150",
                              active
                                ? "text-primary"
                                : "text-sidebar-foreground/55 group-hover:text-sidebar-foreground"
                            )}
                          />
                          {!collapsed && (
                            <span className="truncate">{item.label}</span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* Bottom section */}
        <div className="shrink-0 border-t border-sidebar-border p-2.5">
          <div
            className={cn(
              "flex items-center gap-2",
              collapsed ? "flex-col" : "justify-between"
            )}
          >
            {/* User info */}
            <div
              className={cn(
                "flex min-w-0 items-center gap-2",
                collapsed && "justify-center"
              )}
            >
              <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">
                {user?.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              {!collapsed && (
                <div className="min-w-0">
                  <p className="truncate text-[12.5px] font-medium text-sidebar-foreground">
                    {user?.name || user?.email}
                  </p>
                  <p className="truncate text-[11px] text-sidebar-foreground/50">
                    {role === "admin" ? t("sidebar.roles.admin") : role === "therapist" ? t("sidebar.roles.therapist") : role === "supervisor" ? t("sidebar.roles.supervisor") : t("sidebar.roles.client")}
                  </p>
                </div>
              )}
            </div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="flex size-7 shrink-0 items-center justify-center rounded-lg text-sidebar-foreground/45 transition-colors hover:bg-rose-50 hover:text-rose-600"
              aria-label={t("sidebar.logout")}
            >
              <SignOut className="size-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* ─── Mobile slide-out drawer ─── */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col border-r border-sidebar-border bg-sidebar shadow-xl transition-transform duration-300 ease-in-out md:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Drawer header */}
        <div className="flex h-14 items-center justify-between border-b border-sidebar-border px-4">
          <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
            <Logo className="h-5 w-auto text-sidebar-foreground" />
          </Link>
          <button
            onClick={() => setMobileOpen(false)}
            className="rounded-lg p-1.5 text-sidebar-foreground/50 hover:bg-sidebar-accent hover:text-sidebar-foreground"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Drawer nav */}
        <nav className="flex-1 overflow-y-auto px-3 pb-3">
          {sections.map((section, idx) => {
            const isFirst = idx === 0;
            return (
              <div key={idx}>
                {section.label && (
                  <p
                    className={cn(
                      "px-3 pb-1.5 text-[12px] font-medium tracking-tight text-sidebar-foreground/40",
                      isFirst ? "pt-1" : "pt-5"
                    )}
                  >
                    {section.label}
                  </p>
                )}
                <ul className="flex flex-col gap-0.5">
                  {section.items.map((item) => {
                    const active = isActive(item.href);
                    const Icon = item.icon;
                    return (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          onClick={() => setMobileOpen(false)}
                          className={cn(
                            "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium transition-colors duration-150",
                            active
                              ? "bg-sidebar-accent text-sidebar-foreground"
                              : "text-sidebar-foreground/65 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
                          )}
                        >
                          {active && (
                            <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary" />
                          )}
                          <Icon
                            weight={active ? "fill" : "regular"}
                            className={cn(
                              "size-[22px] shrink-0",
                              active ? "text-primary" : "text-sidebar-foreground/55"
                            )}
                          />
                          {item.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* Drawer footer */}
        <div className="border-t border-sidebar-border p-3">
          <div className="mb-2 flex items-center gap-2">
            <div className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
              {user?.name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-sidebar-foreground">
                {user?.name || user?.email}
              </p>
              <p className="text-xs text-sidebar-foreground/50">
                {role === "admin" ? t("sidebar.roles.admin") : role === "therapist" ? t("sidebar.roles.therapist") : role === "supervisor" ? t("sidebar.roles.supervisor") : t("sidebar.roles.client")}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="w-full justify-start text-sidebar-foreground/60 hover:text-rose-600"
          >
            <SignOut className="size-4" />
            {t("sidebar.logout")}
          </Button>
        </div>
      </aside>

      {/* ─── Main area ─── */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Subscription banner — spans main area, content aligned with page container */}
        {role === "therapist" && <SubscriptionBanner />}

        {/* Mobile top bar — hamburger + logo */}
        <header className="relative flex h-14 shrink-0 items-center border-b border-border bg-background px-4 md:hidden">
          <button
            onClick={() => setMobileOpen(true)}
            className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={t("sidebar.openMenu")}
          >
            <List className="size-5" />
          </button>
          <Link
            to="/dashboard"
            className="absolute left-1/2 -translate-x-1/2 flex items-center"
          >
            <Logo className="h-5 w-auto text-foreground" />
          </Link>
        </header>

        {/* Page content */}
        <main
          className={cn(
            "flex-1 overflow-y-auto p-4 sm:p-6",
            whiteContentBg && "bg-white"
          )}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
